import {
  Add,
  ArrowBack,
  ArrowForward,
  Close,
  Delete,
  DragIndicator,
  Edit,
  FileDownload,
  FileUpload,
  LibraryMusic,
  MoreVert,
  MusicNote,
  NoteAlt,
  OpenInNew,
  Search,
  Tune,
} from "@mui/icons-material";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, LogOut } from "lucide-react";
import LoginModal from "../Components/LoginModal";
import { ChordList, ChordListItem } from "../Entities/ChordListModel";
import { useChordLists } from "../utils/useChordLists";
import { useUser } from "../utils/useUser";
import { normalizeSongUrl, useUrlImports } from "../utils/useUrlImports";

const LIST_COLORS = [
  "#3b82f6", // Blue
  "#10b981", // Emerald
  "#f59e0b", // Amber
  "#ef4444", // Rose
  "#8b5cf6", // Purple
  "#ec4899", // Pink
  "#06b6d4", // Cyan
  "#64748b", // Slate
];

export default function KanbanPage() {
  const { enqueue } = useUrlImports();
  const [urlList, setUrlList] = useState<ChordList | null>(null);
  const [songUrl, setSongUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useUser();
  const {
    lists,
    loading,
    error,
    createList,
    updateList,
    deleteList,
    reorderLists,
    updateListItem,
    deleteListItem,
    moveListItem,
    exportLists,
    importLists,
  } = useChordLists();

  const [searchTerm, setSearchTerm] = useState("");
  const [draggedItem, setDraggedItem] = useState<{
    itemId: number;
    sourceListId: number;
  } | null>(null);
  const [dragOverListId, setDragOverListId] = useState<number | null>(null);
  const [dragOverItemId, setDragOverItemId] = useState<number | null>(null);

  // List Create / Edit State
  const [createListOpen, setCreateListOpen] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListDesc, setNewListDesc] = useState("");
  const [newListColor, setNewListColor] = useState(LIST_COLORS[0]);

  const [editingList, setEditingList] = useState<ChordList | null>(null);
  const [editListName, setEditListName] = useState("");
  const [editListDesc, setEditListDesc] = useState("");
  const [editListColor, setEditListColor] = useState(LIST_COLORS[0]);

  // Card Note Edit State
  const [editingNoteItem, setEditingNoteItem] = useState<ChordListItem | null>(null);
  const [itemNoteText, setItemNoteText] = useState("");

  // Column Menu State
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<{
    el: HTMLElement;
    list: ChordList;
  } | null>(null);

  // Item Menu State
  const [itemMenuAnchor, setItemMenuAnchor] = useState<{
    el: HTMLElement;
    item: ChordListItem;
    listId: number;
  } | null>(null);

  // Login Modal State
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Notifications
  const [snackbar, setSnackbar] = useState<string | null>(null);

  /* =================================================
     Drag and Drop Handlers
  ================================================= */

  const handleDragStart = (e: React.DragEvent, itemId: number, sourceListId: number) => {
    setDraggedItem({ itemId, sourceListId });
    e.dataTransfer.setData("text/plain", JSON.stringify({ itemId, sourceListId }));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, listId: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverListId !== listId) {
      setDragOverListId(listId);
    }
  };

  const handleDragLeave = (_e: React.DragEvent, listId: number) => {
    if (dragOverListId === listId) {
      setDragOverListId(null);
    }
  };

  const handleDragOverItem = (e: React.DragEvent, itemId: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (draggedItem?.itemId !== itemId && dragOverItemId !== itemId) {
      setDragOverItemId(itemId);
    }
  };

  const handleDragLeaveItem = (_e: React.DragEvent, itemId: number) => {
    if (dragOverItemId === itemId) {
      setDragOverItemId(null);
    }
  };

  const handleDropOnItem = async (
    e: React.DragEvent,
    targetItemId: number,
    targetListId: number,
    targetIndex: number
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverItemId(null);
    setDragOverListId(null);

    let itemId: number | null = null;
    let sourceListId: number | null = null;

    if (draggedItem) {
      itemId = draggedItem.itemId;
      sourceListId = draggedItem.sourceListId;
    } else {
      try {
        const data = JSON.parse(e.dataTransfer.getData("text/plain"));
        itemId = data.itemId;
        sourceListId = data.sourceListId;
      } catch {
        // ignore
      }
    }

    if (!itemId || !sourceListId || itemId === targetItemId) {
      setDraggedItem(null);
      return;
    }

    try {
      await moveListItem(itemId, sourceListId, targetListId, targetIndex);
      const targetList = lists.find((l) => l.id === targetListId);
      setSnackbar(
        sourceListId === targetListId
          ? "Item reordered"
          : `Moved chord to "${targetList?.name || "list"}"`
      );
    } catch (err: any) {
      console.error("Failed to move item:", err);
      setSnackbar("Failed to reorder item");
    } finally {
      setDraggedItem(null);
    }
  };

  const handleDropOnList = async (e: React.DragEvent, targetListId: number) => {
    e.preventDefault();
    setDragOverListId(null);
    setDragOverItemId(null);

    let itemId: number | null = null;
    let sourceListId: number | null = null;

    if (draggedItem) {
      itemId = draggedItem.itemId;
      sourceListId = draggedItem.sourceListId;
    } else {
      try {
        const data = JSON.parse(e.dataTransfer.getData("text/plain"));
        itemId = data.itemId;
        sourceListId = data.sourceListId;
      } catch {
        // ignore
      }
    }

    if (!itemId || !sourceListId) return;

    const targetList = lists.find((l) => l.id === targetListId);
    const newPos = targetList ? targetList.items.length : 0;

    try {
      await moveListItem(itemId, sourceListId, targetListId, newPos);
      const targetList = lists.find((l) => l.id === targetListId);
      setSnackbar(
        sourceListId === targetListId
          ? "Item reordered"
          : `Moved chord to "${targetList?.name || "list"}"`
      );
    } catch (err: any) {
      console.error("Failed to move item:", err);
      setSnackbar("Failed to move item");
    } finally {
      setDraggedItem(null);
    }
  };

  /* =================================================
     List CRUD Handlers
  ================================================= */

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    try {
      await createList({
        name: newListName.trim(),
        description: newListDesc.trim() || undefined,
        color: newListColor,
      });
      setNewListName("");
      setNewListDesc("");
      setCreateListOpen(false);
      setSnackbar("New chord list created!");
    } catch (err: any) {
      setSnackbar(err.message || "Failed to create list");
    }
  };

  const handleSaveEditList = async () => {
    if (!editingList || !editListName.trim()) return;
    try {
      await updateList(editingList.id, {
        name: editListName.trim(),
        description: editListDesc.trim() || undefined,
        color: editListColor,
      });
      setEditingList(null);
      setSnackbar("List updated!");
    } catch (err: any) {
      setSnackbar(err.message || "Failed to update list");
    }
  };

  const handleDeleteList = async (listId: number) => {
    try {
      await deleteList(listId);
      setSnackbar("List deleted.");
      setColumnMenuAnchor(null);
    } catch (err: any) {
      setSnackbar(err.message || "Failed to delete list");
    }
  };

  const handleMoveColumn = async (listId: number, direction: "left" | "right") => {
    const currentIndex = lists.findIndex((l) => l.id === listId);
    if (currentIndex < 0) return;
    const targetIndex = direction === "left" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= lists.length) return;

    const newOrder = [...lists];
    const [moved] = newOrder.splice(currentIndex, 1);
    newOrder.splice(targetIndex, 0, moved);

    await reorderLists(newOrder.map((l) => l.id));
    setColumnMenuAnchor(null);
  };

  /* =================================================
     Export / Import JSON Handlers
  ================================================= */

  const handleExportJSON = async () => {
    try {
      await exportLists();
      setSnackbar("Lists exported to JSON successfully!");
    } catch (err: any) {
      console.error("Failed to export lists:", err);
      setSnackbar(err.message || "Failed to export lists");
    }
  };

  const handleImportJSON = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const res = await importLists(parsed);
      const warningsText =
        res.warnings && res.warnings.length > 0
          ? ` (${res.warnings.join("; ")})`
          : "";
      setSnackbar(
        `Import completed! (${res.lists_imported} lists updated, ${res.items_imported} items saved).${warningsText}`
      );
    } catch (err: any) {
      console.error("Failed to import lists JSON:", err);
      setSnackbar(err.message || "Failed to parse and import JSON file");
    } finally {
      e.target.value = "";
    }
  };

  /* =================================================
     Item CRUD Handlers
  ================================================= */

  const handleSaveItemNotes = async () => {
    if (!editingNoteItem) return;
    try {
      await updateListItem(editingNoteItem.id, {
        notes: itemNoteText.trim() || undefined,
      });
      setEditingNoteItem(null);
      setSnackbar("Notes saved successfully!");
    } catch (err: any) {
      setSnackbar(err.message || "Failed to save notes");
    }
  };

  const handleQuickMoveItem = async (
    item: ChordListItem,
    sourceListId: number,
    targetListId: number
  ) => {
    setItemMenuAnchor(null);
    const targetList = lists.find((l) => l.id === targetListId);
    const newPos = targetList ? targetList.items.length : 0;
    try {
      await moveListItem(item.id, sourceListId, targetListId, newPos);
      setSnackbar(`Moved to "${targetList?.name || "list"}"`);
    } catch (err: any) {
      setSnackbar(err.message || "Failed to move item");
    }
  };

  const handleMoveItemUpDown = async (
    item: ChordListItem,
    listId: number,
    direction: "up" | "down"
  ) => {
    setItemMenuAnchor(null);
    const list = lists.find((l) => l.id === listId);
    if (!list) return;
    const itemIndex = list.items.findIndex((i) => i.id === item.id);
    if (itemIndex < 0) return;
    const targetIndex = direction === "up" ? itemIndex - 1 : itemIndex + 1;
    if (targetIndex < 0 || targetIndex >= list.items.length) return;

    try {
      await moveListItem(item.id, listId, listId, targetIndex);
    } catch (err: any) {
      setSnackbar("Failed to reorder item");
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    setItemMenuAnchor(null);
    try {
      await deleteListItem(itemId);
      setSnackbar("Chord removed from list.");
    } catch (err: any) {
      setSnackbar("Failed to remove item");
    }
  };

  /* =================================================
     Filter Songs
  ================================================= */

  const filterItem = (item: ChordListItem) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const titleMatch = item.song?.title?.toLowerCase().includes(term);
    const artistMatch = item.song?.artist?.toLowerCase().includes(term);
    const notesMatch = item.notes?.toLowerCase().includes(term);
    const tagsMatch = item.song?.tags?.some((t) => t.toLowerCase().includes(term));
    return titleMatch || artistMatch || notesMatch || tagsMatch;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f8fafc",
        p: { xs: 2, md: 3 },
      }}
    >
      <Container maxWidth={false} sx={{ px: { xs: 1, md: 2 } }}>
        {/* Top Header Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          flexWrap="wrap"
          gap={2}
          mb={3}
        >
          <Box sx={{ minWidth: 0, maxWidth: "100%", overflowWrap: "anywhere" }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
                  color: "#fff",
                  p: 1,
                  borderRadius: 2,
                  display: "flex",
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)",
                }}
              >
                <LibraryMusic />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" fontWeight="bold" color="text.primary">
                  My Chord Lists
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isAuthenticated && user ? (
                    <>
                      Logged in as: <strong>{user.name}</strong> ({user.email || user.id})
                    </>
                  ) : (
                    "Guest Session (Lists saved locally to offline storage)"
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" sx={{ width: { xs: "100%", md: "auto" }, maxWidth: "100%" }}>
            {/* Search Filter */}
            <TextField
              size="small"
              placeholder="Search in lists..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                width: { xs: "100%", sm: 240 },
                minWidth: 0,
                bgcolor: "#fff",
                borderRadius: 2,
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
              }}
            />

            {/* Auth / Sign In Button */}
            {isAuthenticated ? (
              <Button
                variant="outlined"
                color="inherit"
                onClick={logout}
                startIcon={<LogOut size={16} />}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  bgcolor: "#fff",
                  borderColor: "#cbd5e1",
                  whiteSpace: "nowrap",
                }}
              >
                Sign Out
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setLoginModalOpen(true)}
                startIcon={<LogIn size={16} />}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  bgcolor: "#fff",
                  whiteSpace: "nowrap",
                  fontWeight: 600,
                }}
              >
                Sign In
              </Button>
            )}

            {/* Export JSON Button */}
            <Tooltip title="Export all lists and chords to JSON">
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleExportJSON}
                startIcon={<FileDownload fontSize="small" />}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  bgcolor: "#fff",
                  borderColor: "#cbd5e1",
                  whiteSpace: "nowrap",
                }}
              >
                Export JSON
              </Button>
            </Tooltip>

            {/* Import JSON Button */}
            <Tooltip title="Import lists from JSON file">
              <Button
                variant="outlined"
                color="inherit"
                component="label"
                startIcon={<FileUpload fontSize="small" />}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  bgcolor: "#fff",
                  borderColor: "#cbd5e1",
                  whiteSpace: "nowrap",
                }}
              >
                Import JSON
                <input
                  type="file"
                  hidden
                  accept=".json,application/json"
                  onChange={handleImportJSON}
                />
              </Button>
            </Tooltip>

            {/* New List Button */}
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => {
                setNewListName("");
                setNewListDesc("");
                setNewListColor(LIST_COLORS[lists.length % LIST_COLORS.length]);
                setCreateListOpen(true);
              }}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 600,
                px: 2,
                whiteSpace: "nowrap",
                background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)",
              }}
            >
              New List
            </Button>
          </Box>
        </Box>

        {/* Not Signed In Notice Banner */}
        {!isAuthenticated && (
          <Alert
            severity="info"
            action={
              <Button
                color="primary"
                size="small"
                variant="contained"
                onClick={() => setLoginModalOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
              >
                Sign In with Google
              </Button>
            }
            sx={{
              mb: 2.5,
              borderRadius: 2,
              flexWrap: { xs: "wrap", sm: "nowrap" },
              "& .MuiAlert-message": { minWidth: 0, flex: "1 1 0" },
              "& .MuiAlert-action": {
                width: { xs: "100%", sm: "auto" },
                ml: { xs: 0, sm: "auto" },
                pl: { xs: 0, sm: 2 },
                pb: { xs: 1, sm: 0 },
              },
            }}
          >
            Sign in with Google to sync and preserve your chord lists and practice board across all your devices.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading Spinner */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
          </Box>
        ) : (
          /* Kanban Board */
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2.5,
              pb: 4,
              pt: 1,
              minHeight: "75vh",
              alignItems: "flex-start",
            }}
          >
            {lists.map((list, listIndex) => {
              const filteredItems = list.items.filter(filterItem);
              const isOver = dragOverListId === list.id;

              return (
                <Box
                  key={list.id}
                  onDragOver={(e) => handleDragOver(e, list.id)}
                  onDragLeave={(e) => handleDragLeave(e, list.id)}
                  onDrop={(e) => handleDropOnList(e, list.id)}
                  sx={{
                    flex: { xs: "0 0 100%", sm: "0 0 280px" },
                    width: { xs: "100%", sm: 280 },
                    minWidth: 0,
                    boxSizing: "border-box",
                    bgcolor: isOver ? "#e0f2fe" : "#f1f5f9",
                    borderRadius: 3,
                    display: "flex",
                    flexDirection: "column",
                    border: "2px solid",
                    borderColor: isOver
                      ? "#3b82f6"
                      : "#e2e8f0",
                    transition: "all 0.2s ease",
                    boxShadow: isOver
                      ? "0 8px 24px rgba(59, 130, 246, 0.18)"
                      : "0 2px 8px rgba(0,0,0,0.04)",
                  }}
                >
                  {/* Column Header */}
                  <Box
                    sx={{
                      p: 2,
                      pb: 1.5,
                      borderTop: `4px solid ${list.color || "#3b82f6"}`,
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                      bgcolor: "#fff",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            flexShrink: 0,
                            borderRadius: "50%",
                            bgcolor: list.color || "#3b82f6",
                          }}
                        />
                        <Typography variant="subtitle1" fontWeight="700" color="text.primary" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                          {list.name}
                        </Typography>
                        <Chip
                          label={filteredItems.length}
                          size="small"
                          sx={{
                            height: 20,
                            flexShrink: 0,
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                            bgcolor: "#f1f5f9",
                          }}
                        />
                      </Box>

                      <IconButton
                        size="small"
                        sx={{ flexShrink: 0 }}
                        onClick={(e) =>
                          setColumnMenuAnchor({ el: e.currentTarget, list })
                        }
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>

                    {list.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          mt: 0.5,
                        }}
                      >
                        {list.description}
                      </Typography>
                    )}
                  </Box>

                  {/* Column Cards List */}
                  <Box
                    sx={{
                      p: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {filteredItems.map((item, itemIndex) => {
                      const isItemDropTarget = dragOverItemId === item.id;
                      return (
                        <Card
                          key={item.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, item.id, list.id)}
                          onDragEnd={() => {
                            setDraggedItem(null);
                            setDragOverItemId(null);
                            setDragOverListId(null);
                          }}
                          onDragOver={(e) => handleDragOverItem(e, item.id)}
                          onDragLeave={(e) => handleDragLeaveItem(e, item.id)}
                          onDrop={(e) => handleDropOnItem(e, item.id, list.id, itemIndex)}
                          sx={{
                            bgcolor: "#ffffff",
                            flexShrink: 0,
                            borderRadius: 2,
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                            border: "1px solid",
                            borderColor: isItemDropTarget ? "#2563eb" : "#e2e8f0",
                            borderTop: isItemDropTarget ? "3px solid #2563eb" : "1px solid #e2e8f0",
                            cursor: "grab",
                            transition: "all 0.15s ease",
                            "&:hover": {
                              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                              transform: "translateY(-1px)",
                              borderColor: "#cbd5e1",
                            },
                            "&:active": {
                              cursor: "grabbing",
                            },
                          }}
                        >
                          <CardContent sx={{ px: 1.5, py: 1, "&:last-child": { pb: 1 } }}>
                            {/* Card Header */}
                            <Box
                              display="flex"
                              alignItems="flex-start"
                              justifyContent="space-between"
                              mb={0.5}
                            >
                              <Box display="flex" alignItems="flex-start" gap={0.75} flex={1} sx={{ minWidth: 0 }}>
                                <DragIndicator
                                  fontSize="small"
                                  sx={{ color: "#94a3b8", cursor: "grab", mt: 0.2, fontSize: 18, flexShrink: 0 }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    component={Link}
                                    to={`/Song?id=${item.song?.id || item.song_id}`}
                                    variant="subtitle2"
                                    fontWeight="700"
                                    sx={{
                                      textDecoration: "none",
                                      color: "text.primary",
                                      fontSize: "0.875rem",
                                      lineHeight: 1.3,
                                      "&:hover": {
                                        color: "primary.main",
                                        textDecoration: "underline",
                                      },
                                      display: "-webkit-box",
                                      WebkitLineClamp: 1,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {item.song?.title || `Song #${item.song_id}`}
                                  </Typography>
                                  {item.song?.artist && (
                                    <Typography
                                      component={Link}
                                      to={`/Artist?name=${encodeURIComponent(item.song.artist)}`}
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        textDecoration: "none",
                                        fontSize: "0.75rem",
                                        lineHeight: 1.2,
                                        "&:hover": { color: "primary.main", textDecoration: "underline" },
                                        display: "block",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {item.song.artist}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>

                              <Box display="flex" alignItems="center" gap={0.2} sx={{ flexShrink: 0 }}>
                                {!item.notes && (
                                  <Tooltip title="Add personal note">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setEditingNoteItem(item);
                                        setItemNoteText("");
                                      }}
                                      sx={{
                                        p: 0.5,
                                        color: "#94a3b8",
                                        "&:hover": { color: "#d97706" },
                                      }}
                                    >
                                      <NoteAlt sx={{ fontSize: 17 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    setItemMenuAnchor({ el: e.currentTarget, item, listId: list.id })
                                  }
                                  sx={{ p: 0.5 }}
                                >
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>

                            {/* User Personal Notes Box */}
                            {item.notes && (
                              <Box
                                onClick={() => {
                                  setEditingNoteItem(item);
                                  setItemNoteText(item.notes || "");
                                }}
                                sx={{
                                  bgcolor: "#fffbeb",
                                  border: "1px solid #fde68a",
                                  borderRadius: 1,
                                  p: 0.75,
                                  mt: 0.5,
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                  "&:hover": {
                                    bgcolor: "#fef3c7",
                                  },
                                }}
                              >
                                <Box display="flex" alignItems="center" gap={0.5} mb={0.2}>
                                  <NoteAlt sx={{ fontSize: 13, color: "#d97706" }} />
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    color="#92400e"
                                    sx={{ fontSize: "0.7rem" }}
                                  >
                                    Note:
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="#78350f"
                                  sx={{
                                    display: "block",
                                    whiteSpace: "pre-wrap",
                                    overflowWrap: "anywhere",
                                    fontSize: "0.72rem",
                                    lineHeight: 1.25,
                                  }}
                                >
                                  {item.notes}
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}

                    {filteredItems.length === 0 && (
                      <Box
                        sx={{
                          p: 3,
                          textAlign: "center",
                          border: "2px dashed #cbd5e1",
                          borderRadius: 2,
                          bgcolor: "rgba(255,255,255,0.5)",
                        }}
                      >
                        <MusicNote sx={{ color: "#94a3b8", mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm ? "No matching songs" : "Drop chords here or save from song page"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}

            {/* Quick Add List Column at End */}
            <IconButton
              aria-label="Add another list"
              onClick={() => {
                setNewListName("");
                setNewListDesc("");
                setNewListColor(LIST_COLORS[lists.length % LIST_COLORS.length]);
                setCreateListOpen(true);
              }}
              sx={{
                flex: "0 0 48px",
                width: 48,
                height: 48,
                borderRadius: 3,
                border: "2px dashed #cbd5e1",
                bgcolor: "transparent",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                p: 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#f1f5f9",
                  borderColor: "primary.main",
                },
              }}
            >
              <Add sx={{ fontSize: 32, color: "primary.main" }} />
            </IconButton>
          </Box>
        )}
      </Container>

      {/* =================================================
         Column Action Menu
      ================================================= */}
      <Menu
        anchorEl={columnMenuAnchor?.el}
        open={Boolean(columnMenuAnchor)}
        onClose={() => setColumnMenuAnchor(null)}
      >
        <MenuItem onClick={() => { if (columnMenuAnchor) setUrlList(columnMenuAnchor.list); setSongUrl(""); setUrlError(""); setColumnMenuAnchor(null); }}>
          <ListItemIcon><Add fontSize="small" /></ListItemIcon><ListItemText>Add by URL</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (columnMenuAnchor) {
              setEditingList(columnMenuAnchor.list);
              setEditListName(columnMenuAnchor.list.name);
              setEditListDesc(columnMenuAnchor.list.description || "");
              setEditListColor(columnMenuAnchor.list.color || LIST_COLORS[0]);
              setColumnMenuAnchor(null);
            }
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename / Edit List</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => columnMenuAnchor && handleMoveColumn(columnMenuAnchor.list.id, "left")}
          disabled={lists.findIndex((l) => l.id === columnMenuAnchor?.list.id) === 0}
        >
          <ListItemIcon>
            <ArrowBack fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move Column Left</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => columnMenuAnchor && handleMoveColumn(columnMenuAnchor.list.id, "right")}
          disabled={
            lists.findIndex((l) => l.id === columnMenuAnchor?.list.id) === lists.length - 1
          }
        >
          <ListItemIcon>
            <ArrowForward fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move Column Right</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => columnMenuAnchor && handleDeleteList(columnMenuAnchor.list.id)}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete List</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(urlList)} onClose={() => setUrlList(null)} fullWidth maxWidth="xs">
        <Box component="form" onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          try { const url = normalizeSongUrl(songUrl); if (urlList) enqueue(url, urlList); setUrlList(null); }
          catch (error: any) { setUrlError(error.message); }
        }}>
          <DialogTitle>Add by URL</DialogTitle>
          <DialogContent><TextField autoFocus fullWidth label="Song URL" value={songUrl} onChange={event => setSongUrl(event.target.value)} error={Boolean(urlError)} helperText={urlError || "Tab4U or Ultimate Guitar"} sx={{ mt: 1 }} /></DialogContent>
          <DialogActions><Button onClick={() => setUrlList(null)}>Cancel</Button><Button type="submit" variant="contained" disabled={!songUrl.trim()}>Add</Button></DialogActions>
        </Box>
      </Dialog>

      {/* =================================================
         Item Action Menu
      ================================================= */}
      <Menu
        anchorEl={itemMenuAnchor?.el}
        open={Boolean(itemMenuAnchor)}
        onClose={() => setItemMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (itemMenuAnchor?.item) {
              const sId = itemMenuAnchor.item.song?.id || itemMenuAnchor.item.song_id;
              navigate(`/Song?id=${sId}`);
            }
          }}
        >
          <ListItemIcon>
            <OpenInNew fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open Song Page</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (itemMenuAnchor?.item) {
              setEditingNoteItem(itemMenuAnchor.item);
              setItemNoteText(itemMenuAnchor.item.notes || "");
              setItemMenuAnchor(null);
            }
          }}
        >
          <ListItemIcon>
            <NoteAlt fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Notes</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() =>
            itemMenuAnchor &&
            handleMoveItemUpDown(itemMenuAnchor.item, itemMenuAnchor.listId, "up")
          }
        >
          <ListItemIcon>
            <ArrowBack fontSize="small" sx={{ transform: "rotate(90deg)" }} />
          </ListItemIcon>
          <ListItemText>Move Up</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() =>
            itemMenuAnchor &&
            handleMoveItemUpDown(itemMenuAnchor.item, itemMenuAnchor.listId, "down")
          }
        >
          <ListItemIcon>
            <ArrowForward fontSize="small" sx={{ transform: "rotate(90deg)" }} />
          </ListItemIcon>
          <ListItemText>Move Down</ListItemText>
        </MenuItem>

        <Divider />
        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: "text.secondary", fontWeight: "bold" }}>
          Move to List:
        </Typography>

        {lists
          .filter((l) => l.id !== itemMenuAnchor?.listId)
          .map((targetList) => (
            <MenuItem
              key={targetList.id}
              onClick={() =>
                itemMenuAnchor &&
                handleQuickMoveItem(itemMenuAnchor.item, itemMenuAnchor.listId, targetList.id)
              }
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: targetList.color || "#3b82f6",
                  mr: 1.5,
                }}
              />
              <ListItemText>{targetList.name}</ListItemText>
            </MenuItem>
          ))}

        <Divider />

        <MenuItem
          onClick={() => itemMenuAnchor && handleDeleteItem(itemMenuAnchor.item.id)}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove from List</ListItemText>
        </MenuItem>
      </Menu>

      {/* =================================================
         Create List Dialog
      ================================================= */}
      <Dialog
        open={createListOpen}
        onClose={() => setCreateListOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Create New Chord List</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="List Name"
              placeholder="e.g. Practicing, Sunday Jam, Setlist"
              fullWidth
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              autoFocus
            />
            <TextField
              label="Description (optional)"
              placeholder="Brief description of this list..."
              multiline
              rows={2}
              fullWidth
              value={newListDesc}
              onChange={(e) => setNewListDesc(e.target.value)}
            />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" mb={1} display="block">
                Choose Color:
              </Typography>
              <Box display="flex" gap={1.5} flexWrap="wrap">
                {LIST_COLORS.map((col) => (
                  <Box
                    key={col}
                    onClick={() => setNewListColor(col)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: col,
                      cursor: "pointer",
                      border: newListColor === col ? "3px solid #0f172a" : "3px solid transparent",
                      transform: newListColor === col ? "scale(1.15)" : "scale(1)",
                      transition: "all 0.15s ease",
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateListOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateList}
            disabled={!newListName.trim()}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Create List
          </Button>
        </DialogActions>
      </Dialog>

      {/* =================================================
         Edit List Dialog
      ================================================= */}
      <Dialog
        open={Boolean(editingList)}
        onClose={() => setEditingList(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Edit Chord List</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="List Name"
              fullWidth
              value={editListName}
              onChange={(e) => setEditListName(e.target.value)}
              autoFocus
            />
            <TextField
              label="Description (optional)"
              multiline
              rows={2}
              fullWidth
              value={editListDesc}
              onChange={(e) => setEditListDesc(e.target.value)}
            />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" mb={1} display="block">
                Choose Color:
              </Typography>
              <Box display="flex" gap={1.5} flexWrap="wrap">
                {LIST_COLORS.map((col) => (
                  <Box
                    key={col}
                    onClick={() => setEditListColor(col)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: col,
                      cursor: "pointer",
                      border: editListColor === col ? "3px solid #0f172a" : "3px solid transparent",
                      transform: editListColor === col ? "scale(1.15)" : "scale(1)",
                      transition: "all 0.15s ease",
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditingList(null)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEditList}
            disabled={!editListName.trim()}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* =================================================
         Edit Item Notes Dialog
      ================================================= */}
      <Dialog
        open={Boolean(editingNoteItem)}
        onClose={() => setEditingNoteItem(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Edit Personal Notes
          {editingNoteItem?.song && (
            <Typography variant="body2" color="text.secondary">
              {editingNoteItem.song.title} {editingNoteItem.song.artist ? `• ${editingNoteItem.song.artist}` : ""}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Personal Notes & Annotations"
            multiline
            rows={4}
            fullWidth
            placeholder="e.g., Practice the bridge transition, use capo 2, palm mute verse..."
            value={itemNoteText}
            onChange={(e) => setItemNoteText(e.target.value)}
            sx={{ mt: 1 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditingNoteItem(null)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveItemNotes}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Save Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login Modal */}
      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
      />

      {/* Feedback Snackbar */}
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
